import http from '@/http'
import config from '@/config'

const find = id => {
	const path = `orders/${id}`
	return http.get(path)
}

const mediaToLdReference = (media) => {
	if (media == null) return undefined
	if (typeof media === 'string') return media
	if (typeof media !== 'object') return undefined
	if (media['@id']) return media['@id']
	if (media.id != null && media.id !== '') {
		const base = (config.service.apiURL || '').replace(/\/$/, '')
		return `${base}/media_objects/${media.id}`
	}
	return undefined
}

const normalizeAttachmentsPayload = (attachments) =>
	attachments.map((a, idx) => {
		const mediaRef = mediaToLdReference(a.media)
		return {
			...(a['@id'] ? { '@id': a['@id'] } : {}),
			position: typeof a.position === 'number' ? a.position : idx,
			description: a.description != null ? a.description : '',
			...(mediaRef != null ? { media: mediaRef } : {})
		}
	})

const normalizeAttachmentsPayloadWithoutIds = (attachments) =>
	attachments.map((a, idx) => {
		const mediaRef = mediaToLdReference(a.media)
		return {
			position: typeof a.position === 'number' ? a.position : idx,
			description: a.description != null ? a.description : '',
			...(mediaRef != null ? { media: mediaRef } : {})
		}
	})

const stripReadOnlyOrderKeys = (obj) => {
	const omit = new Set([
		'baselinkerOrderDataJson',
		'attachmentExportRows',
		'hasAttachments',
		'isAttachmentAnImage',
		'clientAddress',
		'hydra:member',
		'hydra:totalItems',
		'hydra:view',
		// JSON-LD metadata leaked from GET responses. Sending '@id' back as part
		// of a POST/PUT body trips API Platform's ItemNormalizer with
		// "Update is not allowed for this operation." (it interprets nested
		// '@id' as a reference to update). We never want to update via body.
		'@id',
		'@type',
		'@context'
	])
	const out = { ...obj }
	omit.forEach((k) => delete out[k])
	return out
}

const getAll = ({
	sorting: { field, order },
	perPage = 10,
	pageNr = 1,
	filter,
	searchText,
	where = []
}) => {
	let filterSegment = ''
	let searchSegment = ''
	let whereSegment = ''

	if (filter) {
		if (typeof filter.value === 'boolean') filterSegment += `&${filter.by}[exists]=${filter.value}`
		else if (Array.isArray(filter.value))
			filter.value.forEach(value => {
				filterSegment += `&${filter.by}[]=${value}`
			})
		else filterSegment += `&${filter.by}=${filter.value}`
	}

	if (where.length) {
		where.forEach(({ whereField, whereValue }) => {
			whereSegment += `&${whereField}=${whereValue}`
		})
	}

	if (searchText) searchSegment = `&search=${searchText}`

	let path = `orders?order[${field}]=${order}&itemsPerPage=${perPage}&page=${pageNr}`
	if (filterSegment) path += filterSegment
	if (searchSegment) path += searchSegment
	if (whereSegment) path += whereSegment

	return http.get(path)
}

const update = order => {
	const path = `orders/${order.id}`
	const basePayload = stripReadOnlyOrderKeys({ ...order })

	if (basePayload.route) basePayload.route = `/api/routes/${basePayload.route.id}`
	if (basePayload.creator) delete basePayload.creator
	if (basePayload.logs) delete basePayload.logs
	if (basePayload.isInvoiceSent === null) delete basePayload.isInvoiceSent
	if (basePayload.isInvoicePaid === null) delete basePayload.isInvoicePaid

	const orderToUpdate = { ...basePayload }
	if (Array.isArray(orderToUpdate.attachments)) {
		// Avoid stale order_attachment references after quick delete/add cycles.
		orderToUpdate.attachments = normalizeAttachmentsPayloadWithoutIds(orderToUpdate.attachments)
	}

	return http.put(path, orderToUpdate)
}

const create = order => {
	const path = `orders`
	const payload = stripReadOnlyOrderKeys({ ...order })
	if (Array.isArray(payload.attachments)) {
		payload.attachments = normalizeAttachmentsPayload(payload.attachments)
	}
	if (payload.creator) delete payload.creator
	return http.post(path, payload)
}

const destroy = order => {
	const path = `orders/${order.id}`
	return http.delete(path)
}

const sendEmail = data => {
	const path = `send-email`
	return http.post(path, data)
}


export default {
	find,
	getAll,
	create,
	update,
	destroy,
	sendEmail
}
